import React, { useEffect, useState } from 'react';
import { ScrollText } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  ADMIN_AUDIT_ACTIONS,
  ADMIN_AUDIT_PAGE_SIZE,
  ADMIN_AUDIT_TARGET_TYPES,
} from '../../constants/adminAudit.js';
import {
  buildAdminAuditRequest,
  isReversedAuditDateRange,
  listAdminAuditLogs,
} from '../../services/admin/auditService';
import { classifyAdminCallableError, formatAdminDate } from './adminUserDisplay';
import {
  ADMIN_DATE_PLACEHOLDER,
  adminAuditActionLabel,
  adminAuditDetailFields,
  adminAuditRoleLabel,
  resolveAuditIdentity,
  resolveAuditTarget,
} from './adminAuditDisplay';

function IdentityBlock({ person, t }) {
  const identity = resolveAuditIdentity(person);
  return (
    <div>
      {identity.displayName ? (
        <p className="font-medium text-slate-900">{identity.displayName}</p>
      ) : null}
      {identity.email ? (
        <p className="break-all text-xs text-slate-500">{identity.email}</p>
      ) : null}
      {identity.uid ? (
        <p className="mt-0.5 break-all text-xs text-slate-400" title={identity.uid}>
          {t('admin.audit.uid')}: {identity.uid}
        </p>
      ) : null}
      {!identity.displayName && !identity.email && !identity.uid ? (
        <p className="text-slate-700">{ADMIN_DATE_PLACEHOLDER}</p>
      ) : null}
    </div>
  );
}

function TargetBlock({ log, t }) {
  const resolved = resolveAuditTarget(log);
  if (resolved.kind === 'user') {
    return <IdentityBlock person={resolved.identity} t={t} />;
  }
  return (
    <div>
      <p className="font-medium text-slate-900">{resolved.targetType}</p>
      {resolved.id ? <p className="break-all text-xs text-slate-500">{resolved.id}</p> : null}
    </div>
  );
}

function ActionBlock({ log, t }) {
  const { label, rawAction } = adminAuditActionLabel(log, t);
  return (
    <div>
      <p className="font-medium text-slate-900">{label}</p>
      {rawAction ? <p className="text-xs text-slate-500">{rawAction}</p> : null}
    </div>
  );
}

function CardField({ label, children }) {
  return (
    <div>
      <dt className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-0.5 break-words text-sm text-slate-800">{children}</dd>
    </div>
  );
}

function AuditDetailsDialog({ log, language, t, onClose }) {
  if (!log) return null;
  const extra = adminAuditDetailFields(log, t);
  const actor = resolveAuditIdentity(log.actor);
  const target = resolveAuditTarget(log);
  const { label, rawAction } = adminAuditActionLabel(log, t);
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="admin-audit-details-title"
    >
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-5 shadow-lg">
        <h2 id="admin-audit-details-title" className="text-lg font-semibold text-slate-900">
          {t('admin.audit.details.title')}
        </h2>
        <dl className="mt-4 space-y-3 text-sm">
          <CardField label={t('admin.audit.details.date')}>
            {formatAdminDate(log.createdAt, language)}
          </CardField>
          <CardField label={t('admin.audit.details.actor')}>
            <IdentityBlock person={actor} t={t} />
          </CardField>
          <CardField label={t('admin.audit.col.role')}>
            {adminAuditRoleLabel(log.actorRole, t)}
          </CardField>
          <CardField label={t('admin.audit.details.action')}>
            <p>{label}</p>
            {rawAction ? <p className="text-xs text-slate-500">{rawAction}</p> : null}
          </CardField>
          <CardField label={t('admin.audit.details.target')}>
            {target.kind === 'user' ? (
              <IdentityBlock person={target.identity} t={t} />
            ) : (
              <div>
                <p>{target.targetType}</p>
                {target.id ? <p className="break-all text-xs text-slate-500">{target.id}</p> : null}
              </div>
            )}
          </CardField>
          {extra.map((row) => (
            <CardField key={row.key} label={row.label}>
              {row.value}
            </CardField>
          ))}
        </dl>
        {extra.length === 0 ? (
          <p className="mt-4 text-sm text-slate-600">{t('admin.audit.details.none')}</p>
        ) : null}
        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {t('admin.audit.details.close')}
          </button>
        </div>
      </div>
    </div>
  );
}

function emptyApplied() {
  return {
    filterType: 'all',
    action: '',
    actorUid: '',
    actor: null,
    targetType: '',
    createdAfter: '',
    createdBefore: '',
  };
}

function requestFromApplied(applied, pageToken) {
  return buildAdminAuditRequest({
    action: applied.filterType === 'action' ? applied.action : undefined,
    actorUid: applied.filterType === 'actor' ? applied.actorUid : undefined,
    targetType: applied.filterType === 'targetType' ? applied.targetType : undefined,
    createdAfter: applied.createdAfter,
    createdBefore: applied.createdBefore,
    pageSize: ADMIN_AUDIT_PAGE_SIZE,
    pageToken,
  });
}

function hasActiveFilters(applied) {
  if (!applied) return false;
  if (applied.filterType === 'action' && applied.action) return true;
  if (applied.filterType === 'actor' && applied.actorUid) return true;
  if (applied.filterType === 'targetType' && applied.targetType) return true;
  if (applied.createdAfter || applied.createdBefore) return true;
  return false;
}

export default function AdminAuditPage() {
  const { t, language } = useLanguage();
  const [filterType, setFilterType] = useState('all');
  const [action, setAction] = useState('');
  const [selectedActor, setSelectedActor] = useState(null);
  const [targetType, setTargetType] = useState(ADMIN_AUDIT_TARGET_TYPES[0]?.value || 'user');
  const [createdAfter, setCreatedAfter] = useState('');
  const [createdBefore, setCreatedBefore] = useState('');
  const [dateError, setDateError] = useState(null);
  const [applied, setApplied] = useState(emptyApplied);
  const [logs, setLogs] = useState([]);
  const [status, setStatus] = useState('loading');
  const [errorKind, setErrorKind] = useState(null);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [tokenStack, setTokenStack] = useState([]);
  const [currentToken, setCurrentToken] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);

  const busy = status === 'loading' || status === 'paging';

  const applyList = (data, nextApplied, { paging = false, nextStack, pageToken } = {}) => {
    const list = Array.isArray(data?.logs) ? data.logs : [];
    setLogs(list);
    setApplied(nextApplied);
    setNextPageToken(data?.nextPageToken || null);
    setCurrentToken(pageToken || null);
    if (Array.isArray(nextStack)) setTokenStack(nextStack);
    else if (!paging) setTokenStack([]);
    setStatus(list.length === 0 ? (hasActiveFilters(nextApplied) ? 'lookup-empty' : 'empty') : 'ready');
  };

  const fail = (err) => {
    setLogs([]);
    setNextPageToken(null);
    setErrorKind(classifyAdminCallableError(err));
    setStatus('error');
  };

  const loadApplied = async (nextApplied, pageToken, { paging = false, nextStack } = {}) => {
    setStatus(paging ? 'paging' : 'loading');
    setErrorKind(null);
    setDateError(null);
    try {
      const data = await listAdminAuditLogs(requestFromApplied(nextApplied, pageToken));
      applyList(data, nextApplied, { paging, nextStack, pageToken });
    } catch (err) {
      fail(err);
    }
  };

  useEffect(() => {
    loadApplied(emptyApplied(), undefined);
  }, []);

  const snapshotForm = () => ({
    filterType,
    action: filterType === 'action' ? action : '',
    actorUid: filterType === 'actor' ? selectedActor?.uid || '' : '',
    actor: filterType === 'actor' ? selectedActor : null,
    targetType: filterType === 'targetType' ? targetType : '',
    createdAfter,
    createdBefore,
  });

  const canApply = () => {
    if (isReversedAuditDateRange(createdAfter, createdBefore)) return false;
    if (filterType === 'action') return Boolean(action);
    if (filterType === 'actor') return Boolean(selectedActor?.uid);
    if (filterType === 'targetType') return Boolean(targetType);
    return true;
  };

  const handleApply = (event) => {
    event.preventDefault();
    if (isReversedAuditDateRange(createdAfter, createdBefore)) {
      setDateError(t('admin.audit.error.reversedDates'));
      return;
    }
    if (!canApply()) return;
    setTokenStack([]);
    setCurrentToken(null);
    loadApplied(snapshotForm());
  };

  const handleReset = () => {
    setFilterType('all');
    setAction('');
    setSelectedActor(null);
    setTargetType(ADMIN_AUDIT_TARGET_TYPES[0]?.value || 'user');
    setCreatedAfter('');
    setCreatedBefore('');
    setDateError(null);
    setTokenStack([]);
    setCurrentToken(null);
    setSelectedLog(null);
    setErrorKind(null);
    loadApplied(emptyApplied());
  };

  const handleRefresh = () => {
    setTokenStack([]);
    setCurrentToken(null);
    loadApplied(applied);
  };

  const handleFilterByActor = (actor) => {
    const identity = resolveAuditIdentity(actor);
    if (!identity.uid) return;
    const nextActor = {
      uid: identity.uid,
      displayName: identity.displayName,
      email: identity.email,
    };
    setFilterType('actor');
    setSelectedActor(nextActor);
    setAction('');
    setDateError(null);
    setTokenStack([]);
    setCurrentToken(null);
    loadApplied({
      filterType: 'actor',
      action: '',
      actorUid: identity.uid,
      actor: nextActor,
      targetType: '',
      createdAfter,
      createdBefore,
    });
  };

  const handleClearActor = () => {
    setSelectedActor(null);
    setFilterType('all');
    setDateError(null);
    setTokenStack([]);
    setCurrentToken(null);
    loadApplied({
      ...emptyApplied(),
      createdAfter,
      createdBefore,
    });
  };

  const handleFilterTypeChange = (nextType) => {
    setFilterType(nextType);
    setDateError(null);
    if (nextType !== 'actor') setSelectedActor(null);
    if (nextType !== 'action') setAction('');
    if (nextType === 'targetType' && !targetType) {
      setTargetType(ADMIN_AUDIT_TARGET_TYPES[0]?.value || 'user');
    }
  };

  const handleNext = () => {
    if (busy || !nextPageToken) return;
    const nextStack = [...tokenStack, currentToken];
    loadApplied(applied, nextPageToken, { paging: true, nextStack });
  };

  const handlePrevious = () => {
    if (busy || tokenStack.length === 0) return;
    const nextStack = tokenStack.slice(0, -1);
    const previousToken = tokenStack[tokenStack.length - 1];
    loadApplied(applied, previousToken || undefined, { paging: true, nextStack });
  };

  const errorMessage =
    errorKind === 'unavailable'
      ? t('admin.audit.error.unavailable')
      : errorKind === 'permission-denied'
        ? t('admin.audit.error.permission')
        : errorKind === 'failed-precondition'
          ? t('admin.audit.error.index')
          : errorKind === 'invalid-argument'
            ? t('admin.audit.error.invalid')
            : t('admin.audit.error');

  const showPagination =
    status === 'ready' || status === 'paging' || status === 'empty' || status === 'lookup-empty';

  const actorFilterLabel = selectedActor
    ? resolveAuditIdentity(selectedActor).primary
    : '';

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <ScrollText className="mt-0.5 h-5 w-5 shrink-0 text-indigo-600" aria-hidden="true" />
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-xl font-semibold text-slate-900">{t('admin.audit.title')}</h1>
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                {t('admin.audit.readOnly')}
              </span>
            </div>
            <p className="mt-1 text-sm text-slate-600">{t('admin.audit.subtitle')}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={busy}
          className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
        >
          {t('admin.audit.refresh')}
        </button>
      </div>

      <form
        onSubmit={handleApply}
        className="space-y-3 rounded-xl border border-slate-200 bg-white p-4"
        aria-label={t('admin.audit.filters')}
      >
        <p className="text-sm font-semibold text-slate-800">{t('admin.audit.filters')}</p>
        <div className="flex flex-col gap-2 lg:flex-row lg:items-end">
          <label className="text-sm lg:w-56">
            <span className="mb-1 block text-slate-600">{t('admin.audit.filter.type')}</span>
            <select
              id="admin-audit-filter-type"
              value={filterType}
              onChange={(event) => handleFilterTypeChange(event.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">{t('admin.audit.filter.type.all')}</option>
              <option value="action">{t('admin.audit.filter.type.action')}</option>
              <option value="actor">{t('admin.audit.filter.type.actor')}</option>
              <option value="targetType">{t('admin.audit.filter.type.targetType')}</option>
            </select>
          </label>
          {filterType === 'action' ? (
            <label className="min-w-0 flex-1 text-sm">
              <span className="mb-1 block text-slate-600">{t('admin.audit.filter.action')}</span>
              <select
                id="admin-audit-filter-action"
                value={action}
                onChange={(event) => setAction(event.target.value)}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">{t('admin.audit.filter.action.choose')}</option>
                {ADMIN_AUDIT_ACTIONS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {t(item.filterLabelKey)}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {filterType === 'actor' ? (
            <div className="min-w-0 flex-1 text-sm">
              <p className="mb-1 text-slate-600">{t('admin.audit.filter.type.actor')}</p>
              {selectedActor?.uid ? (
                <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                  <IdentityBlock person={selectedActor} t={t} />
                  <button
                    type="button"
                    onClick={handleClearActor}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {t('admin.audit.filter.actor.clear')}
                  </button>
                </div>
              ) : (
                <p className="rounded-lg border border-dashed border-slate-300 px-3 py-2 text-slate-600">
                  {t('admin.audit.filter.actor.hint')}
                </p>
              )}
              {actorFilterLabel ? <span className="sr-only">{actorFilterLabel}</span> : null}
            </div>
          ) : null}
          {filterType === 'targetType' ? (
            <label className="min-w-0 flex-1 text-sm">
              <span className="mb-1 block text-slate-600">{t('admin.audit.filter.targetType')}</span>
              <select
                id="admin-audit-filter-target-type"
                value={targetType}
                onChange={(event) => setTargetType(event.target.value)}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {ADMIN_AUDIT_TARGET_TYPES.map((item) => (
                  <option key={item.value} value={item.value}>
                    {t(item.labelKey)}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <label className="min-w-0 flex-1 text-sm">
            <span className="mb-1 block text-slate-600">{t('admin.audit.filter.createdAfter')}</span>
            <input
              id="admin-audit-created-after"
              type="date"
              value={createdAfter}
              onChange={(event) => {
                setCreatedAfter(event.target.value);
                setDateError(null);
              }}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </label>
          <label className="min-w-0 flex-1 text-sm">
            <span className="mb-1 block text-slate-600">{t('admin.audit.filter.createdBefore')}</span>
            <input
              id="admin-audit-created-before"
              type="date"
              value={createdBefore}
              onChange={(event) => {
                setCreatedBefore(event.target.value);
                setDateError(null);
              }}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              type="submit"
              disabled={busy || !canApply()}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
            >
              {t('admin.audit.apply')}
            </button>
            <button
              type="button"
              onClick={handleReset}
              disabled={busy && status === 'loading'}
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
            >
              {t('admin.audit.reset')}
            </button>
          </div>
        </div>
        {dateError ? <p className="text-sm text-red-700">{dateError}</p> : null}
      </form>

      {status === 'loading' ? (
        <p className="text-sm text-slate-500" aria-live="polite">
          {t('admin.audit.loading')}
        </p>
      ) : null}
      {status === 'paging' ? (
        <p className="text-sm text-slate-500" aria-live="polite">
          {t('admin.audit.paging')}
        </p>
      ) : null}
      {status === 'empty' ? (
        <p className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
          {t('admin.audit.empty')}
        </p>
      ) : null}
      {status === 'lookup-empty' ? (
        <p className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
          {t('admin.audit.lookupEmpty')}
        </p>
      ) : null}
      {status === 'error' ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <p>{errorMessage}</p>
          <button
            type="button"
            onClick={() => loadApplied(applied, currentToken || undefined)}
            className="mt-2 rounded-lg border border-red-300 bg-white px-3 py-1.5 text-sm font-semibold text-red-800 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            {t('admin.audit.retry')}
          </button>
        </div>
      ) : null}

      {logs.length > 0 && (status === 'ready' || status === 'paging') ? (
        <>
          <div className="hidden overflow-x-auto rounded-xl border border-slate-200 bg-white md:block">
            <table className="min-w-full text-left text-sm">
              <caption className="sr-only">{t('admin.audit.title')}</caption>
              <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th scope="col" className="px-3 py-2 font-semibold">{t('admin.audit.col.when')}</th>
                  <th scope="col" className="px-3 py-2 font-semibold">{t('admin.audit.col.actor')}</th>
                  <th scope="col" className="px-3 py-2 font-semibold">{t('admin.audit.col.role')}</th>
                  <th scope="col" className="px-3 py-2 font-semibold">{t('admin.audit.col.action')}</th>
                  <th scope="col" className="px-3 py-2 font-semibold">{t('admin.audit.col.target')}</th>
                  <th scope="col" className="px-3 py-2 font-semibold">{t('admin.audit.col.details')}</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((item) => (
                  <tr key={item.id} className="border-b border-slate-100 last:border-0">
                    <td className="px-3 py-2 whitespace-nowrap">{formatAdminDate(item.createdAt, language)}</td>
                    <td className="px-3 py-2">
                      <IdentityBlock person={item.actor} t={t} />
                      {item.actor?.uid ? (
                        <button
                          type="button"
                          onClick={() => handleFilterByActor(item.actor)}
                          className="mt-1 font-semibold text-indigo-600 hover:underline focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          {t('admin.audit.filter.actor.apply')}
                        </button>
                      ) : null}
                    </td>
                    <td className="px-3 py-2">
                      <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700">
                        {adminAuditRoleLabel(item.actorRole, t)}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <ActionBlock log={item} t={t} />
                    </td>
                    <td className="px-3 py-2">
                      <TargetBlock log={item} t={t} />
                    </td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() => setSelectedLog(item)}
                        className="font-semibold text-indigo-600 hover:underline focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        {t('admin.audit.details.open')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="space-y-3 md:hidden">
            {logs.map((item) => (
              <article key={item.id} className="rounded-xl border border-slate-200 bg-white p-4 text-sm">
                <p className="text-xs text-slate-500">{formatAdminDate(item.createdAt, language)}</p>
                <div className="mt-2">
                  <ActionBlock log={item} t={t} />
                </div>
                <dl className="mt-3 grid grid-cols-1 gap-3">
                  <CardField label={t('admin.audit.col.actor')}>
                    <IdentityBlock person={item.actor} t={t} />
                  </CardField>
                  <CardField label={t('admin.audit.col.target')}>
                    <TargetBlock log={item} t={t} />
                  </CardField>
                </dl>
                <div className="mt-3 flex flex-col items-start gap-2">
                  {item.actor?.uid ? (
                    <button
                      type="button"
                      onClick={() => handleFilterByActor(item.actor)}
                      className="font-semibold text-indigo-600 hover:underline focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      {t('admin.audit.filter.actor.apply')}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => setSelectedLog(item)}
                    className="font-semibold text-indigo-600 hover:underline focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {t('admin.audit.details.open')}
                  </button>
                </div>
              </article>
            ))}
          </div>
        </>
      ) : null}

      {showPagination ? (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handlePrevious}
            disabled={busy || tokenStack.length === 0}
            className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
          >
            {t('admin.audit.previous')}
          </button>
          <button
            type="button"
            onClick={handleNext}
            disabled={busy || !nextPageToken}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
          >
            {t('admin.audit.next')}
          </button>
        </div>
      ) : null}

      <AuditDetailsDialog
        log={selectedLog}
        language={language}
        t={t}
        onClose={() => setSelectedLog(null)}
      />
    </div>
  );
}
